import type { TrustedDevice, TrustedDeviceStatus } from "../types";

function readString(value: unknown): string | undefined {
  return typeof value === "string" ? value : undefined;
}

function readRecordId(o: Record<string, unknown>): string | undefined {
  return readString(o._id) ?? readString(o.id);
}

const STATUSES: TrustedDeviceStatus[] = ["pending", "active", "revoked"];

function parseStatus(raw: unknown): TrustedDeviceStatus | undefined {
  const s = readString(raw)?.toLowerCase();
  if (!s) return undefined;
  return STATUSES.find((st) => st === s);
}

export function parseTrustedDevice(raw: unknown): TrustedDevice | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const id = readRecordId(o);
  const deviceId = readString(o.deviceId);
  if (!id || !deviceId) return null;

  const status = parseStatus(o.status) ?? "pending";

  let userId: TrustedDevice["userId"] | undefined;
  const userRaw = o.user ?? o.userId ?? o.employee;
  if (typeof userRaw === "string") {
    userId = userRaw;
  } else if (userRaw && typeof userRaw === "object") {
    const u = userRaw as Record<string, unknown>;
    const uid = readRecordId(u);
    if (uid) {
      userId = {
        _id: uid,
        name: readString(u.name) ?? readString(u.fullName),
        email: readString(u.email) ?? readString(u.companyEmail),
      };
    }
  }

  // Lean list responses may omit populated user; still parse the device row.
  if (!userId) {
    userId = "—";
  }

  return {
    _id: id,
    deviceId,
    userId,
    status,
    userAgent: readString(o.userAgent),
    platform: readString(o.platform),
    timezone: readString(o.timezone),
    language: readString(o.language),
    screen: readString(o.screen),
    label: readString(o.label),
    createdAt: readString(o.createdAt),
    updatedAt: readString(o.updatedAt),
    registeredAt: readString(o.registeredAt) ?? readString(o.createdAt),
  };
}

export function extractTrustedDeviceArray(payload: unknown): TrustedDevice[] {
  if (Array.isArray(payload)) {
    return payload
      .map(parseTrustedDevice)
      .filter((d): d is TrustedDevice => d !== null);
  }
  if (!payload || typeof payload !== "object") return [];
  const o = payload as Record<string, unknown>;

  for (const key of ["data", "devices", "trustedDevices", "items", "results"] as const) {
    const val = o[key];
    if (Array.isArray(val)) {
      return val
        .map(parseTrustedDevice)
        .filter((d): d is TrustedDevice => d !== null);
    }
    if (val && typeof val === "object" && !Array.isArray(val)) {
      const inner = val as Record<string, unknown>;
      for (const innerKey of ["devices", "items", "list", "rows"] as const) {
        const arr = inner[innerKey];
        if (Array.isArray(arr)) {
          return arr
            .map(parseTrustedDevice)
            .filter((d): d is TrustedDevice => d !== null);
        }
      }
      const single = parseTrustedDevice(val);
      if (single) return [single];
    }
  }

  const single = parseTrustedDevice(payload);
  return single ? [single] : [];
}

export function extractTrustedDevice(payload: unknown): TrustedDevice | null {
  const list = extractTrustedDeviceArray(payload);
  return list[0] ?? null;
}
